import { createContext, ReactNode, useCallback, useContext } from 'react'
import { useLocalStorageQuery } from '../../utils/useLocalStorageQuery'

/** Provider that manages the side navigation expand/collapse state (persisted to localStorage). */
export function NavigationProvider({ children }: { children: ReactNode }) {
  const [expanded, setExpanded] = useLocalStorageQuery(
    'navigation-expanded',
    isBoolean,
    true
  )

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev)
  }, [setExpanded])

  return (
    <NavigationContext.Provider value={{ expanded, toggleExpanded }}>
      {children}
    </NavigationContext.Provider>
  )
}

/** Hook to access the navigation expand/collapse state. */
export function useNavigation() {
  const value = useContext(NavigationContext)
  if (!value) {
    throw new Error('useNavigation must be used within a NavigationProvider')
  }
  return value
}

const isBoolean = (raw: unknown): raw is boolean => typeof raw === 'boolean'

/** Value provided by {@link NavigationProvider} and consumed via {@link useNavigation}. */
interface NavigationContextValue {
  /** Whether the side navigation is currently expanded. */
  expanded: boolean
  /** Toggles the side navigation between expanded and collapsed states. */
  toggleExpanded: () => void
}

const NavigationContext = createContext<NavigationContextValue | null>(null)
